<template>
  <div class="register-form">
    <div class="register-form-title">
      <h2>{{ $t("loginCard.title") }}</h2>
    </div>

    <div class="register-panel">
      <!-- Email/密碼輸入 -->
      <div class="login-email">
        <a-form :model="formModel" autocomplete="off">
          <a-form-item class="custom-form-item custom-input" name="email" :validate-status="formErrors.email.status"
            :help="formErrors.email.message" :rules="[
              { required: true, message: $t('loginCard.emailRequired') },
              { type: 'email', message: $t('loginCard.emailInvalid') },
            ]">
            <label class="form-label" for="email">
              {{ $t("loginCard.email") }}<span class="required">*</span>
            </label>
            <a-input style="margin-top: 4px" id="email" name="email" autocomplete="off"
              :placeholder="$t('loginCard.emailPlaceholder')" :title="$t('loginCard.email')"
              v-model:value="formModel.email" @input="clearFormErrors" />
          </a-form-item>

          <a-form-item class="custom-form-item custom-input password-input" name="password"
            :validate-status="formErrors.password.status" :help="formErrors.password.message" :rules="[
              { required: true, message: $t('loginCard.passwordRequired') },
            ]" style="margin-top: 30px">
            <label class="form-label" for="password">
              {{ $t("loginCard.password") }}<span class="required">*</span>
            </label>
            <a-input-password style="margin-top: 4px" id="password" name="password" autocomplete="new-password"
              :placeholder="$t('loginCard.passwordPlaceholder')" :title="$t('loginCard.password')"
              v-model:value="formModel.password" @input="clearFormErrors" />
          </a-form-item>
        </a-form>

        <div class="hint-and-remember">
          <a-checkbox
            v-model:checked="formModel.remember"
            class="remember-me"
            :title="$t('loginCard.rememberMe')"
            :aria-label="$t('loginCard.rememberMe')"
          >{{
            $t("loginCard.rememberMe")
          }}</a-checkbox>
          <router-link to="/forgot-password">{{
            $t("loginCard.forgotPassword")
          }}</router-link>
        </div>
      </div>

      <!-- 登入按鈕與註冊 -->
      <div class="login-with-email">
        <a-button class="create-w-email-btn" :loading="loading" @click="handleLogin">{{ $t("loginCard.loginButton")
        }}</a-button>
        <div class="toc">
          <span class="toc-item">{{ $t("loginCard.noAccount") }}</span>
          <router-link to="/register" class="toc-link">{{
            $t("loginCard.register")
          }}</router-link>
        </div>
      </div>
    </div>
  </div>

  <!-- 2FA 驗證碼 Modal -->
  <div v-if="twoFactorModalVisible" id="twoFactorModal" class="modal-overlay" @click="handleTwoFactorCancel">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3>{{ $t("loginCard.twoFactorTitle") }}</h3>
        <button class="modal-close" @click="handleTwoFactorCancel">
          &times;
        </button>
      </div>
      <div class="modal-body">
        <p>{{ $t("loginCard.twoFactorDescription") }}</p>
        <div class="form-group">
          <input v-model="twoFactorCode" type="text" :placeholder="$t('loginCard.twoFactorPlaceholder')" maxlength="8"
            @input="clearTwoFactorErrors" class="two-factor-input"
            :class="{ 'input-error': twoFactorErrors.status === 'error' }" />
          <div v-if="twoFactorErrors.message" class="error-message">
            {{ twoFactorErrors.message }}
          </div>
          <div class="resend-section">
            <button @click="handleResend2FA" :disabled="resendCountdown > 0 || resendLoading" class="btn-resend">
              <span v-if="resendLoading">Loading</span>
              <span v-else-if="resendCountdown > 0">
                {{ $t("loginCard.resendCodeIn", { seconds: resendCountdown }) }}
              </span>
              <span v-else>{{ $t("loginCard.resendCode") }}</span>
            </button>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-cancel" @click="handleTwoFactorCancel" :disabled="twoFactorLoading">
          {{ $t("loginCard.twoFactorCancel") }}
        </button>
        <button class="btn btn-submit" @click="handleTwoFactorSubmit" :disabled="twoFactorLoading">
          <span v-if="twoFactorLoading">Loading...</span>
          <span v-else>{{ $t("loginCard.twoFactorSubmit") }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted } from "vue";
import { message, Modal } from "ant-design-vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { authAPI } from "@/services/api.js";

const router = useRouter();
const { t } = useI18n();
const loginForm = ref(null);
const loading = ref(false);

const mockDelay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const mockupData = {
  login: async ({ email }) => {
    await mockDelay(500);
    if (!email) {
      return { data: { status: "error", message: t("loginCard.checkEmail") } };
    }
    return { data: { status: "2fa_required" } };
  },
  verify2FACode: async (code) => {
    await mockDelay(500);
    if (String(code).length !== 8) {
      return { data: { status: "error", message: "invalid_code" } };
    }
    return {
      data: {
        status: "success",
        content: { original: { access_token: "mock_access_token" } },
      },
    };
  },
  resend2FA: async () => {
    await mockDelay(500);
    return { data: { status: "success" } };
  },
  getUserInfo: async () => {
    await mockDelay(200);
    return { data: { content: { id: 1, name: "Mock User" } } };
  },
};

// 表單錯誤狀態
const formErrors = reactive({
  email: {
    status: "",
    message: "",
  },
  password: {
    status: "",
    message: "",
  },
});

const formModel = reactive({
  email: "",
  password: "",
  remember: false,
});

// 2FA 相關狀態
const twoFactorModalVisible = ref(false);
const twoFactorLoading = ref(false);
const twoFactorCode = ref("");
const twoFactorErrors = reactive({
  status: "",
  message: "",
});
const pendingLoginData = ref(null); // 儲存待驗證的登入資料
const resendCountdown = ref(0); // 重新寄送倒數計時
const resendLoading = ref(false); // 重新寄送載入狀態

// 清除表單錯誤狀態
const clearFormErrors = () => {
  formErrors.email.status = "";
  formErrors.email.message = "";
  formErrors.password.status = "";
  formErrors.password.message = "";
};

// 清除2FA錯誤狀態
const clearTwoFactorErrors = () => {
  twoFactorErrors.status = "";
  twoFactorErrors.message = "";
};

// 處理2FA驗證碼提交
const handleTwoFactorSubmit = async () => {
  if (!twoFactorCode.value || twoFactorCode.value.length !== 8) {
    twoFactorErrors.status = "error";
    twoFactorErrors.message = "請輸入8位數驗證碼";
    return;
  }

  try {
    twoFactorLoading.value = true;
    clearTwoFactorErrors();

    const response = await mockupData.verify2FACode(twoFactorCode.value);
    if (response.data && response.data.status === "success") {
      const token = response.data.content.original.access_token;

      if (token) {
        localStorage.setItem("ms_platform_token", token);

        try {
          const userResponse = await mockupData.getUserInfo();
          if (userResponse && userResponse.data) {
            localStorage.setItem(
              "user_data",
              JSON.stringify(userResponse.data)
            );
          }
        } catch (userError) {
          console.error("獲取用戶資訊失敗:", userError);
        }

        // 關閉模態框
        twoFactorModalVisible.value = false;
        twoFactorCode.value = "";

        message.success(t("loginCard.loginSuccess"));

        // 處理記住我功能
        if (pendingLoginData.value?.remember) {
          localStorage.setItem(
            "remembered_email",
            pendingLoginData.value.email
          );
        } else {
          localStorage.removeItem("remembered_email");
        }

        // 導向到首頁或上次訪問的頁面
        const lastVisitedPath = localStorage.getItem("lastVisitedPath");
        if (lastVisitedPath) {
          router.push(lastVisitedPath);
        } else {
          router.push("/");
        }
      } else {
        throw new Error("無法取得存取權杖");
      }
    } else {
      // API 回傳失敗
      twoFactorErrors.status = "error";
      twoFactorErrors.message =
        response.data?.message || "驗證失敗，請檢查驗證碼";
    }
  } catch (error) {
    twoFactorErrors.status = "error";
    twoFactorErrors.message = "驗證失敗，請重新嘗試";
  } finally {
    twoFactorLoading.value = false;
  }
};

// 重新發送 2FA 驗證碼
const handleResend2FA = async () => {
  if (!pendingLoginData.value?.email) {
    message.error(t("loginCard.resend2FAError"));
    return;
  }

  try {
    resendLoading.value = true;

    const response = await mockupData.resend2FA();

    if (response.data && response.data.status === "success") {
      message.success(t("loginCard.resendCodeSuccess"));

      // 開始倒數計時（60秒）
      resendCountdown.value = 60;

      // 清除輸入的驗證碼
      twoFactorCode.value = "";
      clearTwoFactorErrors();

      // 設定倒數計時器
      const countdownInterval = setInterval(() => {
        resendCountdown.value--;
        if (resendCountdown.value <= 0) {
          clearInterval(countdownInterval);
        }
      }, 1000);
    } else {
      message.error(response.data?.message || t("loginCard.resendCodeFailed"));
    }
  } catch (error) {
    message.error(t("loginCard.resendCodeError"));
  } finally {
    resendLoading.value = false;
  }
};

// 取消2FA驗證
const handleTwoFactorCancel = () => {
  twoFactorModalVisible.value = false;
  twoFactorCode.value = "";
  clearTwoFactorErrors();
  pendingLoginData.value = null;
};

const handleLogin = async () => {
  try {
    loading.value = true;
    // 清除之前的錯誤狀態
    clearFormErrors();

    const loginData = {
      email: formModel.email,
      password: formModel.password,
    };

    const response = await authAPI.login(loginData);

    if (response.data?.status === "error") {
      if (response.data?.content?.errors) {
        const errors = response.data.content.errors;
        if (errors.email && errors.email.length > 0) {
          formErrors.email.status = "error";
          formErrors.email.message = errors.email[0];
        }
        if (errors.password && errors.password.length > 0) {
          formErrors.password.status = "error";
          formErrors.password.message = errors.password[0];
        }
      }
      message.error(response.data.message || "資料驗證失敗");
      return;
    }

    if (response.status === 200) {
      // 2026-08-06：檢查是否需要補綁手機
      const requiresBinding = response.data?.requires_mobile_binding === true;
      const token = response.data?.token;
      if (token) {
        localStorage.setItem("ms_platform_token", token);
      }

      try {
        const userResponse = await authAPI.me();
        if (userResponse?.data) {
          localStorage.setItem("user_data", JSON.stringify(userResponse.data));
        }
      } catch (userError) {
        console.error("獲取用戶資訊失敗:", userError);
      }

      if (requiresBinding) {
        message.warning(t("smsAuth.bindTitle") || "請補綁手機");
        router.push("/bind-mobile");
        return;
      }

      if (formModel.remember) {
        localStorage.setItem("remembered_email", formModel.email);
      } else {
        localStorage.removeItem("remembered_email");
      }

      const proceedNavigation = () => {
        const lastVisitedPath = localStorage.getItem("lastVisitedPath");
        if (lastVisitedPath) {
          router.push(lastVisitedPath);
        } else {
          router.push("/");
        }
      };

      // 措施 4：檢查密碼是否已超過 180 天過期
      const isPasswordExpired =
        response.data?.password_expired === true ||
        response.data?.content?.password_expired === true;
      const passwordExpiredInfo =
        response.data?.password_expired_info ||
        response.data?.content?.password_expired_info;

      if (isPasswordExpired) {
        const expiredMsg =
          passwordExpiredInfo?.message ||
          "您的密碼已超過 180 天最長使用期限，請立即更換密碼！";
        message.error(expiredMsg);
        router.push("/ProfileEdit?tab=security");
        return;
      }

      // 措施 4：檢查密碼到期預警（150～179天）
      const passwordWarning =
        response.data?.password_warning ||
        response.data?.content?.password_warning;

      if (passwordWarning) {
        const warningMsg =
          passwordWarning.message ||
          `您上次變更密碼為 ${passwordWarning.days_since_last_change} 天前，密碼將於 ${passwordWarning.days_remaining} 天後到期，建議您儘速變更密碼。`;
        message.warning(warningMsg);
      }

      message.success(t("loginCard.loginSuccess"));
      proceedNavigation();
    } else {
      message.error(response.data?.message || "登入失敗，請檢查您的帳號和密碼");
    }
  } catch (error) {
    // 措施 4：攔截 PASSWORD_EXPIRED 錯誤
    if (
      error?.response?.status === 403 &&
      (error?.response?.data?.content?.code === "PASSWORD_EXPIRED" ||
        error?.response?.data?.code === "PASSWORD_EXPIRED")
    ) {
      const expiredMsg =
        error?.response?.data?.message ||
        "您的密碼已超過 180 天最長使用期限，請立即更換密碼！";
      message.error(expiredMsg);
      router.push("/ProfileEdit?tab=security");
      return;
    }

    if (
      error?.response?.status === 403 &&
      error?.response?.data?.code === "EMAIL_NOT_VERIFIED"
    ) {
      sessionStorage.setItem("registerEmail", formModel.email);
      message.warning(error?.response?.data?.message || "請先完成 Email 驗證");
      router.push("/emailvalidate");
      return;
    }

    // 2026-08-06：攔截 PHONE_NOT_VERIFIED → /phone-validate
    if (
      error?.response?.status === 403 &&
      error?.response?.data?.code === "PHONE_NOT_VERIFIED"
    ) {
      const phone = error.response.data.content?.phone;
      const email = error.response.data.content?.email;
      if (email) sessionStorage.setItem("registerEmail", email);
      if (phone) sessionStorage.setItem("registerPhone", phone);
      message.warning(error?.response?.data?.message || "請先完成手機驗證");
      router.push("/phone-validate");
      return;
    }

    if (error?.response?.data?.content?.errors) {
      const errors = error.response.data.content.errors;
      if (errors.email && errors.email.length > 0) {
        formErrors.email.status = "error";
        formErrors.email.message = errors.email[0];
      }
      if (errors.password && errors.password.length > 0) {
        formErrors.password.status = "error";
        formErrors.password.message = errors.password[0];
      }
      message.error(error.response.data.message || "資料驗證失敗");
      return;
    }

    message.error(error?.response?.data?.message || t("loginCard.loginFailed"));
  } finally {
    loading.value = false;
  }
};
// 頁面載入時檢查是否有記住的帳號
onMounted(() => {
  const rememberedEmail = localStorage.getItem("remembered_email");
  if (rememberedEmail) {
    formModel.email = rememberedEmail;
    formModel.remember = true;
  }
});
</script>

<style scoped>
.register-form {
  width: 100%;
  max-width: 550px;
  border-radius: 20px;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 36px 32px;
  box-sizing: border-box;
  max-height: 600px;
}

.register-form-title {
  text-align: center;
  margin-bottom: 3cqmin;
}

.register-form-title .description {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-primary);
  margin: 8px 0 0 0;
  text-align: center;
  line-height: 1.5;
}

.register-form-title h2 {
  font-size: 30px;
  font-weight: 700;
  color: #000;
  margin: 0;
}

.register-form-title p {
  font-size: 18px;
  font-weight: 400;
  color: #797979;
  margin: 0;
}

.register-panel {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.login-email {
  display: flex;
  flex-direction: column;
}

.login-email input#email,
.login-email input#password {
  font-size: 16px !important;
}

.custom-form-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.form-label {
  font-size: 16px;
  font-weight: 500;
  color: #000;
}

.required {
  color: #ff4d4f;
  margin-left: 2px;
}

.custom-input {
  height: auto;
  margin: 0;
  padding: 0;
}

.custom-input :deep(.ant-input),
.custom-input :deep(.ant-input-password) {
  height: 64px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  padding-right: 16px;
  /* 🔧 讓眼睛 icon 有間距 */
  background-color: #fff;
  box-shadow: none !important;
}

/* 針對密碼輸入框內的 input 本體 */
.custom-input :deep(.ant-input-password input.ant-input) {
  border: none;
  box-shadow: none;
  height: 100%;
  font-size: 16px;
  flex: 1;
  background-color: transparent;
}

.custom-input :deep(.ant-input::placeholder),
.custom-input :deep(.ant-input-password input::placeholder) {
  color: #9c9c9c;
  opacity: 1;
}

/* 眼睛 icon 對齊 */
.custom-input :deep(.ant-input-password-icon) {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #707070;
}

.hint-and-remember {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  width: 100%;
}

.hint-and-remember :deep(.ant-checkbox-wrapper) {
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
}

.remember-me {
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.login-with-email {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.create-w-email-btn {
  width: 100%;
  height: 64px;
  border-radius: 999px;
  background-color: var(--color-primary);
  color: #ffffff;
  font-size: 18px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toc {
  font-size: 16px;
  color: #414143;
  width: 100%;
  text-align: center;
}

.toc-item {
  display: inline;
}

.toc-link {
  color: #0a1843;
  text-decoration: underline;
  font-weight: 500;
  text-align: center;
  padding: 10px 20px;
}

/* ✅ RWD 響應 */
@media (max-width: 1024px) {
  .register-form {
    padding: 32px 24px;
    max-height: 550px;
  }

  .register-form-title h2 {
    font-size: 24px;
  }

  .register-form-title p {
    font-size: 0.9em;
  }

  .custom-input :deep(.ant-input),
  .custom-input :deep(.ant-input-password) {
    height: 56px !important;
    font-size: 15px !important;
  }

  .create-w-email-btn {
    height: 48px;
    font-size: 16px;
    border-radius: 40px;
  }

  .login-devider span {
    font-size: 0.9em;
  }

  .social-btn {
    height: 48px;
    font-size: 15px;
  }

  .hint-and-remember {
    font-size: 0.9em;
    width: 100%;
  }

  .toc {
    display: inline-block;
    text-align: center;
    font-size: 0.9em;
    width: 100%;
  }

  .toc-link {
    text-align: center;
  }
}

/* 原生 Modal 樣式 */
#twoFactorModal.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
}

#twoFactorModal .modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease-out;
}

#twoFactorModal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

#twoFactorModal .modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

#twoFactorModal .modal-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

#twoFactorModal .modal-close:hover {
  background-color: #f5f5f5;
  color: #666;
}

#twoFactorModal .modal-body {
  padding: 24px;
}

#twoFactorModal .modal-body p {
  margin: 0 0 16px 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

#twoFactorModal .form-group {
  margin-bottom: 0;
}

#twoFactorModal .two-factor-input {
  width: 100%;
  height: 48px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 0 16px;
  font-size: 16px;
  transition: all 0.3s;
  text-align: center;
}

#twoFactorModal .two-factor-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px var(--color-primary-focus);
}

#twoFactorModal .two-factor-input.input-error {
  border-color: #ff4d4f;
}

#twoFactorModal .error-message {
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
  min-height: 16px;
}

#twoFactorModal .resend-section {
  margin-top: 16px;
  text-align: center;
}

#twoFactorModal .btn-resend {
  background: none;
  border: none;
  color: var(--color-primary);
  font-size: 14px;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 6px;
  transition: all 0.2s;
  font-weight: 500;
}

#twoFactorModal .btn-resend:hover:not(:disabled) {
  background-color: var(--color-primary-focus);
  color: var(--color-primary-hover);
}

#twoFactorModal .btn-resend:disabled {
  color: #999;
  cursor: not-allowed;
  background: none;
}

#twoFactorModal .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
}

#twoFactorModal .btn {
  padding: 8px 20px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

#twoFactorModal .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

#twoFactorModal .btn-cancel {
  background: #fff;
  border-color: #d9d9d9;
  color: #666;
}

#twoFactorModal .btn-cancel:hover:not(:disabled) {
  border-color: #999;
  color: #333;
}

#twoFactorModal .btn-submit {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #fff;
}

#twoFactorModal .btn-submit:hover:not(:disabled) {
  background: var(--color-primary-hover);
  border-color: var(--color-primary-hover);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
